import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../../components/Head/SeoTags";

const Import = () => {
  const { t } = useTranslation();

  const browsers = [
    { name: "Chrome", icon: "bi-browser-chrome", color: "text-warning" },
    { name: "Firefox", icon: "bi-browser-firefox", color: "text-danger" },
    { name: "Safari", icon: "bi-browser-safari", color: "text-primary" },
    { name: "Edge", icon: "bi-browser-edge", color: "text-info" },
    { name: "Brave", icon: "bi-shield-check", color: "text-warning" },
    { name: "Opera", icon: "bi-browser-chrome", color: "text-danger" },
  ];

  const services = [
    { name: "Raindrop.io", icon: "bi-droplet-fill" },
    { name: "Pocket", icon: "bi-bag-fill" },
    { name: "Instapaper", icon: "bi-bookmark-fill" },
    { name: "Pinboard", icon: "bi-pin-fill" },
  ];

  return (
    <>
      <SeoTags
        title="Import Bookmarks - Migrate from Any Browser or Service | onBookmarks"
        description="Easily import your bookmarks from Chrome, Firefox, Safari, Edge, Brave, Raindrop.io, Pocket, and more. One-click migration preserves your organization."
        keywords="import bookmarks, migrate bookmarks, chrome bookmarks, firefox bookmarks, safari bookmarks, raindrop import, pocket import, bookmark migration"
        canonical="https://onbookmarks.com/features/import/"
        localized={false}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Import Bookmarks - Migrate from Any Browser or Service",
            description: "Easily import your bookmarks from Chrome, Firefox, Safari, Edge, Brave, Raindrop.io, Pocket, and more.",
            url: "https://onbookmarks.com/features/import/",
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "ProductivityApplication",
              operatingSystem: "Web, iOS, Android",
              featureList: [
                "Import from all major browsers",
                "Raindrop.io migration",
                "Pocket migration",
                "Preserve folder structure",
                "AI-powered re-organization"
              ],
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                description: "Free plan includes unlimited imports"
              }
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://onbookmarks.com"
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Features",
                  item: "https://onbookmarks.com/features/"
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: "Import",
                  item: "https://onbookmarks.com/features/import/"
                }
              ]
            }
          }),
        }}
      />

      {/* Hero Section */}
      <section className="hero bg-purple-subtle">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="badge bg-purple text-white px-3 py-2">
                    <i className="bi bi-cloud-upload me-2"></i>
                    One-Click Migration
                  </span>
                </div>
                <h1 className="display-4 fw-bold">
                  Import Your Bookmarks in Seconds
                </h1>
                <p className="lead">
                  Bring all your bookmarks from any browser or service. Our smart importer preserves your organization and adds AI-powered suggestions.
                </p>
                <div className="d-flex gap-3 flex-wrap">
                  <Link
                    to={`${process.env.REACT_APP_APP_URL}/register`}
                    className="btn btn-primary btn-lg px-4"
                  >
                    Start Importing Free
                  </Link>
                  <a href="#how-it-works" className="btn btn-outline-primary btn-lg px-4">
                    See How It Works
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <div className="text-center">
                <div className="bg-white rounded-4 shadow-lg p-5">
                  <i className="bi bi-cloud-arrow-up display-1 text-purple mb-3"></i>
                  <p className="h5 mb-4">Supported Sources</p>
                  <div className="d-flex flex-wrap justify-content-center gap-4">
                    {browsers.map((browser) => (
                      <div key={browser.name} className="text-center">
                        <i className={`bi ${browser.icon} fs-2 ${browser.color}`}></i>
                        <small className="d-block text-muted mt-1">{browser.name}</small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Browser Support Section */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center mb-5">
              <h2 className="h1 fw-bold mb-4">
                Import from Any Source
              </h2>
              <p className="lead text-muted">
                Whether you're coming from a browser or another bookmark service, we've got you covered.
              </p>
            </div>
          </div>

          <div className="row g-4 mb-5">
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-4">
                    <i className="bi bi-window fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">From Browsers</h3>
                  </div>
                  <p className="text-muted mb-4">
                    Export your bookmarks from any browser and import them with one click. We preserve your folder structure and organize everything automatically.
                  </p>
                  <div className="d-flex flex-wrap gap-3">
                    {browsers.map((browser) => (
                      <span key={browser.name} className="badge bg-light text-dark py-2 px-3">
                        <i className={`bi ${browser.icon} me-2 ${browser.color}`}></i>
                        {browser.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center mb-4">
                    <i className="bi bi-box-arrow-in-down fs-3 text-purple me-3"></i>
                    <h3 className="h5 mb-0">From Services</h3>
                  </div>
                  <p className="text-muted mb-4">
                    Moving from another bookmark manager? We support imports from all major services. Your tags, collections, and metadata are preserved.
                  </p>
                  <div className="d-flex flex-wrap gap-3">
                    {services.map((service) => (
                      <span key={service.name} className="badge bg-light text-dark py-2 px-3">
                        <i className={`bi ${service.icon} me-2 text-purple`}></i>
                        {service.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h1 fw-bold mb-4">
              Three Simple Steps
            </h2>
            <p className="lead text-muted">
              Migration is quick and painless. Your bookmarks will be organized in minutes.
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">1</span>
                </div>
                <h3 className="h5 mb-3">Export Your Bookmarks</h3>
                <p className="text-muted">
                  Go to your browser's bookmark manager and export as HTML. Coming from Pocket, Raindrop, or Instapaper? Their CSV exports work too — tags, folders, and dates included.
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">2</span>
                </div>
                <h3 className="h5 mb-3">Upload to onBookmarks</h3>
                <p className="text-muted">
                  Drag and drop your export file or click to upload. We'll instantly analyze and preview what will be imported.
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center">
                <div className="rounded-circle bg-purple-subtle d-inline-flex align-items-center justify-content-center mb-3 step-circle">
                  <span className="fs-2 fw-bold text-purple">3</span>
                </div>
                <h3 className="h5 mb-3">Done! Start Organizing</h3>
                <p className="text-muted">
                  Your bookmarks are imported with their original structure. Our AI suggests additional organization to make finding things even easier.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="h1 fw-bold mb-4">
                Smart Import Features
              </h2>
              <p className="lead mb-4">
                Our importer doesn't just copy your bookmarks - it makes them better.
              </p>
              <ul className="list-unstyled">
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">Preserve Folder Structure</h4>
                    <p className="text-muted mb-0">
                      Your folders become collections. Nested folders are supported too.
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">AI-Powered Suggestions</h4>
                    <p className="text-muted mb-0">
                      After import, our AI suggests additional collections and tags.
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">Duplicate Detection</h4>
                    <p className="text-muted mb-0">
                      We automatically identify and handle duplicate bookmarks.
                    </p>
                  </div>
                </li>
                <li className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success fs-5 me-3 mt-1"></i>
                  <div>
                    <h4 className="h6 mb-1">Metadata Preservation</h4>
                    <p className="text-muted mb-0">
                      Keep your tags, descriptions, and creation dates intact.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="col-lg-6">
              <div className="bg-light rounded-4 p-4">
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <i className="bi bi-file-earmark-check text-success fs-4 me-3"></i>
                      <div>
                        <h6 className="mb-0">bookmarks_chrome_export.html</h6>
                        <small className="text-muted">245 KB</small>
                      </div>
                    </div>
                    <div className="row text-center">
                      <div className="col-6 border-end">
                        <div className="fs-3 fw-bold text-primary">1,247</div>
                        <small className="text-muted">Bookmarks</small>
                      </div>
                      <div className="col-6">
                        <div className="fs-3 fw-bold text-secondary">34</div>
                        <small className="text-muted">Collections</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="d-grid">
                  <button className="btn btn-success btn-lg" disabled>
                    <i className="bi bi-download me-2"></i>
                    Import 1,247 Bookmarks
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pocket Migration Callout */}
      <section className="py-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="card border-0 bg-danger bg-opacity-10">
                <div className="card-body p-4 d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <i className="bi bi-arrow-repeat fs-3 text-danger"></i>
                    <div>
                      <h4 className="h6 mb-1">Coming from Pocket?</h4>
                      <p className="text-muted mb-0 small">With Pocket shutting down, now is the perfect time to migrate. We preserve your saves, tags, and reading status.</p>
                    </div>
                  </div>
                  <Link to="/pocket-alternative" className="btn btn-danger btn-sm text-nowrap">
                    Pocket Migration Guide
                    <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5">
        <div className="container">
          <div className="bg-purple rounded-4 p-5 text-center">
            <h2 className="h1 fw-bold mb-4 text-white">
              Ready to Bring Your Bookmarks Home?
            </h2>
            <p className="lead mb-4 text-white">
              Migrate your bookmarks to a smarter, more organized home.
            </p>
            <Link
              to={`${process.env.REACT_APP_APP_URL}/register`}
              className="btn btn-light btn-lg px-5"
            >
              Start Free Import
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Import;
